import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { FC, useState, useEffect } from 'react';
import { useJobseekerAddReferenceMutation, useJobseekerUpdateReferenceMutation } from '@carcareers/store';
import { useAuth } from '@carcareers/hooks';
import { JobseekerReference } from '@libs/models';
import { ApiError } from '@libs/types';

interface JobseekerReferenceModalProps {
  type: 'add' | 'edit';
  open: boolean;
  onClose: () => void;
  reference?: JobseekerReference;
}

export const JobseekerReferenceModal: FC<JobseekerReferenceModalProps> = ({ type, open, onClose, reference }) => {
  const { session } = useAuth();
  const [addReference, { isLoading: isAdding }] = useJobseekerAddReferenceMutation();
  const [updateReference, { isLoading: isUpdating }] = useJobseekerUpdateReferenceMutation();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    relationship: '',
  });

  const [errors, setErrors] = useState<ApiError[]>([]);

  useEffect(() => {
    if (type === 'edit' && reference) {
      setFormData({
        name: reference.name || '',
        email: reference.email || '',
        phone: reference.phone || '',
        position: reference.position || '',
        relationship: reference.relationship || '',
      });
    } else if (type === 'add') {
      setFormData({
        name: '',
        email: '',
        phone: '',
        position: '',
        relationship: '',
      });
    }
    setErrors([]);
  }, [type, reference, open]);

  const getFieldError = (fieldName: string): string => {
    const fieldError = errors.find(error => error.property === fieldName);
    return fieldError?.errors?.[0] || '';
  };

  const hasFieldError = (fieldName: string): boolean => {
    return !!getFieldError(fieldName);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear errors for this field when user starts typing
    if (hasFieldError(field)) {
      setErrors(prev => prev.filter(error => error.property !== field));
    }
  };

  const handleSubmit = async () => {
    if (!session?.profile?.id) return;

    setErrors([]);

    try {
      if (type === 'add') {
        await addReference({
          jobseekerId: session.profile.id,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          position: formData.position,
          relationship: formData.relationship,
        }).unwrap();
      } else if (type === 'edit' && reference) {
        await updateReference({
          id: Number(reference.id),
          jobseekerId: Number(session.profile.id),
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          position: formData.position,
          relationship: formData.relationship,
        }).unwrap();
      }

      if (type === 'add') {
        setFormData({
          name: '',
          email: '',
          phone: '',
          position: '',
          relationship: '',
        });
      }
      onClose();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error?.data?.errors) {
        setErrors(error.data.errors);
      }
    }
  };

  const isFormValid = formData.name && formData.email && formData.phone && formData.position && formData.relationship;
  const isLoading = isAdding || isUpdating;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 4,
          },
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">{type === 'add' ? 'Add Reference' : 'Edit Reference'}</Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <TextField
            label="Full Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            fullWidth
            required
            error={hasFieldError('name')}
            helperText={getFieldError('name')}
            sx={{ borderRadius: 4 }}
            InputProps={{
              sx: { borderRadius: 4 },
            }}
          />
          <TextField
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            fullWidth
            required
            error={hasFieldError('email')}
            helperText={getFieldError('email')}
            sx={{ borderRadius: 4 }}
            InputProps={{
              sx: { borderRadius: 4 },
            }}
          />
          <TextField
            label="Phone Number"
            type="tel"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            fullWidth
            required
            placeholder="e.g., (555) 123-4567"
            error={hasFieldError('phone')}
            helperText={getFieldError('phone')}
            sx={{ borderRadius: 4 }}
            InputProps={{
              sx: { borderRadius: 4 },
            }}
          />
          <TextField
            label="Position"
            value={formData.position}
            onChange={(e) => handleInputChange('position', e.target.value)}
            fullWidth
            required
            placeholder="e.g., Senior Manager, Director"
            error={hasFieldError('position')}
            helperText={getFieldError('position')}
            sx={{ borderRadius: 4 }}
            InputProps={{
              sx: { borderRadius: 4 },
            }}
          />
          <TextField
            label="Relationship"
            value={formData.relationship}
            onChange={(e) => handleInputChange('relationship', e.target.value)}
            fullWidth
            required
            placeholder="e.g., Former Supervisor, Colleague, Manager"
            error={hasFieldError('relationship')}
            helperText={getFieldError('relationship')}
            sx={{ borderRadius: 4 }}
            InputProps={{
              sx: { borderRadius: 4 },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid || isLoading}
          sx={{ borderRadius: 2 }}
        >
          {isLoading
            ? type === 'add'
              ? 'Adding...'
              : 'Updating...'
            : type === 'add'
            ? 'Add Reference'
            : 'Update Reference'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 